import { add, scale } from "vector";
import transformPt from "../utils/transform-pt.js";

function textOutline(
  ctx,
  ctr,
  text,
  multiline,
  width,
  annoScale,
  alignment,
  fontSize,
  lineHeight,
  tha,
) {
  let w = 0;
  let h = 0;
  if (width != null) {
    w = width + fontSize * 0.5;
  }

  const padding = fontSize * 0.5 * annoScale;

  if (multiline) {
    const lines = text.split("\n");
    let maxW = 0;
    lines.forEach((line) => {
      const mt = ctx.measureText(line);
      if (mt.width > maxW) {
        maxW = mt.width;
      }
    });
    if (width == null) w = maxW * annoScale;
    h = fontSize * lineHeight * lines.length * annoScale;
  } else {
    const mt = ctx.measureText(text);
    if (width == null) w = mt.width * annoScale;
    h = fontSize * lineHeight * annoScale;
  }

  let x, y;
  if (alignment === "center") {
    x = ctr.x - w / 2;
    y = ctr.y - h + tha + fontSize * 0.5 * annoScale;
  } else {
    x = ctr.x;
    y = ctr.y - h + tha + fontSize * 0.5 * annoScale;
  }

  w = w + padding;
  h = h + padding;
  x = x - padding / 2;
  y = y - padding / 2;

  return { x, y, w, h };
}

class Text {
  constructor(
    text,
    p,
    {
      rotation = 0,
      offset = { x: 0, y: 0 },
      alignment = "center",
      font = "Menlo",
      width = null,
      truncate = false,
      multiline = false,
      annotative = true,
      outline = false,
      highlighted = false,
      scale: ascale = 1,
    } = {},
  ) {
    this.text = text;
    this.p = p;
    this.rotation = rotation;
    this.offset = offset;
    this.type = "text";
    this.font = font;
    this.alignment = alignment;
    this.width = width;
    this.truncate = truncate;
    this.multiline = multiline;
    this.annotative = annotative;
    this.scale = ascale;
    this.outline = outline;
    this.highlighted = highlighted;
  }

  render(options) {
    const ctx = options.ctx;
    const annoScale = this.annotative
      ? options.annoScale * this.scale
      : this.scale;
    const ctr = add(this.p, scale(this.offset, annoScale));
    ctx.style("fontSize", options.fontSize);
    ctx.style("font", this.font);
    ctx.style("annoScale", annoScale);
    ctx.style("width", this.width);
    ctx.style("truncate", this.truncate);
    const mt = ctx.measureText(this.text);
    const tha =
      ((mt.actualBoundingBoxAscent - mt.actualBoundingBoxDescent) / 2) *
      annoScale;

    // Draw text
    ctx.style("fill", options.textColor);
    ctx.style("textAlign", this.alignment);
    ctx.style("fontSize", options.fontSize);
    ctx.style("lineHeight", options.lineHeight);
    ctx.style("fontWeight", options.fontWeight);
    ctx.style("multiline", this.multiline);
    ctx.text(this.text ?? "", ctr.x, ctr.y - tha, this.rotation);

    // Draw outline
    if (this.outline) {
      const { x, y, w, h } = textOutline(
        ctx,
        ctr,
        this.text,
        this.multiline,
        this.width,
        annoScale,
        this.alignment,
        options.fontSize,
        options.lineHeight,
        tha,
      );

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + w, y);
      ctx.lineTo(x + w, y + h);
      ctx.lineTo(x, y + h);
      ctx.lineTo(x, y);
      ctx.closePath();
      ctx.style("lineWidth", options.lineWidth * annoScale);
      ctx.style("stroke", options.textColor);
      ctx.stroke();

      if (this.highlighted) {
        const o = options.lineWidth * annoScale * (1 + options.highlightOffset);

        ctx.beginPath();
        ctx.moveTo(x - o, y - o);
        ctx.lineTo(x + w + o, y - o);
        ctx.lineTo(x + w + o, y + h + o);
        ctx.lineTo(x - o, y + h + o);
        ctx.lineTo(x - o, y - o);
        ctx.closePath();
        ctx.style("lineWidth", options.highlightedLineWidth * annoScale);
        ctx.style("stroke", options.highlightColor);
        ctx.stroke();
      }
    }
  }

  renderHitbox(options) {
    const ctx = options.ctx;
    const annoScale = this.annotative
      ? options.annoScale * this.scale
      : this.scale;
    const ctr = add(this.p, scale(this.offset, annoScale));

    ctx.style("fontSize", options.fontSize);
    ctx.style("font", this.font);
    ctx.style("annoScale", annoScale);
    ctx.style("width", this.width);
    ctx.style("truncate", this.truncate);

    const mt = ctx.measureText(this.text);
    const tha =
      ((mt.actualBoundingBoxAscent - mt.actualBoundingBoxDescent) / 2) *
      annoScale;

    const { x, y, w, h } = textOutline(
      ctx,
      ctr,
      this.text,
      this.multiline,
      this.width,
      annoScale,
      this.alignment,
      options.fontSize,
      options.lineHeight,
      tha,
    );

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + w, y);
    ctx.lineTo(x + w, y + h);
    ctx.lineTo(x, y + h);
    ctx.lineTo(x, y);
    ctx.closePath();
    ctx.style("fill", options.fill);
    ctx.fill();
  }

  get bbox() {
    return {
      xmin: this.p.x,
      xmax: this.p.x,
      ymin: this.p.y,
      ymax: this.p.y,
    };
  }

  transform(matrix) {
    const ctr = transformPt(this.p, matrix);
    return new Text(this.text, ctr, {
      rotation: this.rotation,
      offset: this.offset,
      alignment: this.alignment,
    });
  }
}

export default Text;
